/**
 * Package sizes on the ship step: the vendor's saved box / poly bag sizes
 * (`Company.integrationSettings.shippingDimensionOptions`, set up in admin Settings → Shipping
 * Methods) or a size entered by hand for this one package — typically the manufacturer's own
 * carton, which rarely matches a saved box. Everything is in INCHES, like the saved list. Pure.
 */

export type PackageType = 'box' | 'bag';
export type PackageSizeMode = 'saved' | 'custom';

/** A custom size as typed: raw strings, so a half-typed or blank field stays what it is. */
export type CustomDimensionsInput = { length?: string; width?: string; height?: string };
export type CustomDimensionField = keyof CustomDimensionsInput;

/** One package as the Shipping page holds it (and as a failed ship's draft restores it). */
export type ShipPackageDraft = {
  type: PackageType;
  /** Saved-size key (`16x16x16`), used when `sizeMode` is not `custom`. */
  size?: string;
  /** Absent on packages entered before custom sizes existed (a restored draft): those are saved. */
  sizeMode?: PackageSizeMode;
  custom?: CustomDimensionsInput;
  weight?: number | string;
  weightEntered?: boolean;
  weightKept?: boolean;
};

/** UPS and FedEx both cap a parcel at 108 in on its longest side. */
export const PACKAGE_DIMENSION_MAX_INCHES = 108;

/** The size a new first package starts on, when the vendor has it saved (the long-standing default). */
const PREFERRED_DEFAULT_BOX = '16x16x16';

const FIELD_LABELS: Record<CustomDimensionField, string> = {
  length: 'Length',
  width: 'Width',
  height: 'Height'
};

/** Box: length × width × height. Poly bag: length × width (a bag has no fixed height). */
export const customDimensionFields = (type: PackageType): CustomDimensionField[] =>
  type === 'bag' ? ['length', 'width'] : ['length', 'width', 'height'];

const isPositiveMeasure = (n: unknown): n is number =>
  typeof n === 'number' && Number.isFinite(n) && n > 0;

/** Key a saved size is selected by (also what older drafts store in `size`). */
export const dimensionKey = (dimensions: number[]) => dimensions.join('x');

/** `24 × 18 × 12 in` — how a size reads everywhere on the page. */
export const formatDimensions = (dimensions: number[]) => `${dimensions.join(' × ')} in`;

const volumeOf = (dimensions: number[]) =>
  dimensions.reduce((product, n) => product * n, 1);

/**
 * Largest first: by volume (area for a poly bag), then by the longest side, then the next one —
 * so two boxes of equal volume still list in a stable, sensible order.
 */
export const compareDimensionsDescending = (a: number[], b: number[]) => {
  const byVolume = volumeOf(b) - volumeOf(a);
  if (byVolume) return byVolume;
  const sidesA = [...a].sort((x, y) => y - x);
  const sidesB = [...b].sort((x, y) => y - x);
  for (let i = 0; i < Math.max(sidesA.length, sidesB.length); i += 1) {
    const bySide = (sidesB[i] ?? 0) - (sidesA[i] ?? 0);
    if (bySide) return bySide;
  }
  return 0;
};

/**
 * The saved sizes that fit this package type, largest first. A saved entry with a blank, zero or
 * negative side (admin used to accept those) cannot buy a label, so it is not offered; duplicates
 * are listed once.
 */
export const savedSizesFor = (options: unknown, type: PackageType): number[][] => {
  const sides = type === 'bag' ? 2 : 3;
  const seen = new Set<string>();
  const sizes: number[][] = [];
  for (const option of Array.isArray(options) ? options : []) {
    if (!Array.isArray(option) || option.length !== sides) continue;
    const dimensions = option.map((n) => (typeof n === 'string' ? Number(n) : n));
    if (!dimensions.every(isPositiveMeasure)) continue;
    const key = dimensionKey(dimensions);
    if (seen.has(key)) continue;
    seen.add(key);
    sizes.push(dimensions);
  }
  return sizes.sort(compareDimensionsDescending);
};

/** Select options for the saved sizes: `[key, label]`, largest first. */
export const savedSizeOptions = (sizes: number[][]) =>
  sizes.map((dimensions) => [dimensionKey(dimensions), formatDimensions(dimensions)]);

/**
 * What a package of this type starts on: 16 × 16 × 16 when the vendor has it, else the largest
 * saved size — or a custom size when nothing usable is saved for this type.
 */
export const defaultSizeFor = (
  options: unknown,
  type: PackageType
): Pick<ShipPackageDraft, 'size' | 'sizeMode'> => {
  const sizes = savedSizesFor(options, type);
  if (!sizes.length) return { size: undefined, sizeMode: 'custom' };
  const preferred = sizes.find(
    (dimensions) => dimensionKey(dimensions) === PREFERRED_DEFAULT_BOX
  );
  return { size: dimensionKey(preferred || sizes[0]), sizeMode: 'saved' };
};

/**
 * A new package. The first one starts on the default box; each one after it starts as a copy of
 * the last package's type and size (multi-carton shipments are usually the same carton), with the
 * weight left to weigh.
 */
export const newPackage = (
  options: unknown,
  previous?: ShipPackageDraft
): ShipPackageDraft => {
  if (previous) {
    return {
      type: previous.type,
      size: previous.size,
      sizeMode: previous.sizeMode,
      ...(previous.custom ? { custom: { ...previous.custom } } : {}),
      weight: 0
    };
  }
  return { type: 'box', ...defaultSizeFor(options, 'box'), weight: 0 };
};

/**
 * Switching between box and poly bag. A saved size of one type does not fit the other, so the
 * size moves to the new type's default; a custom size keeps what was typed (a bag just drops the
 * height).
 */
export const changePackageType = (
  pkg: ShipPackageDraft,
  type: PackageType,
  options: unknown
): ShipPackageDraft => {
  if (pkg.type === type) return pkg;
  if (pkg.sizeMode === 'custom') return { ...pkg, type };
  return { ...pkg, type, ...defaultSizeFor(options, type) };
};

/** Parse one typed side. `undefined` value means it was rejected, with `error` saying why. */
export const parseDimension = (
  field: CustomDimensionField,
  raw: unknown
): { value?: number; error?: string } => {
  const label = FIELD_LABELS[field];
  const text = String(raw ?? '').trim();
  if (!text) return { error: `Enter the ${label.toLowerCase()}.` };
  // Digits with an optional decimal part only: "12", "12.5", ".5". `Number()` alone would also
  // take "1e2" or "0x1F" → misleading sizes, so it never sees anything else.
  const unsigned = /^(\d+\.?\d*|\.\d+)$/;
  if (/^-/.test(text) && unsigned.test(text.slice(1))) {
    return { error: `${label} must be greater than 0.` };
  }
  if (!unsigned.test(text)) {
    return { error: `${label} must be a number of inches.` };
  }
  const value = Number(text);
  if (!(value > 0)) return { error: `${label} must be greater than 0.` };
  if (value > PACKAGE_DIMENSION_MAX_INCHES) {
    return {
      error: `${label} can't be more than ${PACKAGE_DIMENSION_MAX_INCHES} in (the UPS / FedEx limit).`
    };
  }
  return { value };
};

export type CustomDimensionsResult = {
  /** One message per rejected side. Empty when the size is good. */
  errors: Partial<Record<CustomDimensionField, string>>;
  /** The size as entered (inches, may be fractional) — present only when every side is good. */
  dimensions?: number[];
};

export const validateCustomDimensions = (
  custom: CustomDimensionsInput | undefined,
  type: PackageType
): CustomDimensionsResult => {
  const errors: CustomDimensionsResult['errors'] = {};
  const values: number[] = [];
  for (const field of customDimensionFields(type)) {
    const { value, error } = parseDimension(field, custom?.[field]);
    if (error) errors[field] = error;
    else values.push(value as number);
  }
  return Object.keys(errors).length ? { errors } : { errors, dimensions: values };
};

/**
 * The carriers rate and label in WHOLE inches and round a fraction UP (and the ship mutation's
 * `dimensions` is `[Int]`), so 12¼ in goes out as 13. Rounding down would under-declare the
 * carton and earn a carrier adjustment. Floating noise below 1/100 in is ignored first.
 */
export const carrierDimensions = (dimensions: number[]) =>
  dimensions.map((n) => Math.ceil(Math.round(n * 100) / 100));

/** The package's size in inches as the operator sees it, or `null` when it is not usable yet. */
export const packageDimensions = (pkg: ShipPackageDraft | undefined): number[] | null => {
  if (!pkg) return null;
  if (pkg.sizeMode === 'custom')
    return validateCustomDimensions(pkg.custom, pkg.type).dimensions ?? null;
  if (!pkg.size) return null;
  const dimensions = String(pkg.size).split('x').map(Number);
  return dimensions.every(isPositiveMeasure) ? dimensions : null;
};

/**
 * PACKING: the package's size once it can ship. At packing a new size is added to the vendor's
 * package library before the package uses it (`companyShippingDimensionOptionAdd`), so a package
 * still on a size being typed (`sizeMode: 'custom'`) has none yet. (The Box Sizes correction page
 * keeps one-off custom sizes and reads `packageDimensions` instead.)
 */
export const packingDimensions = (pkg: ShipPackageDraft | undefined): number[] | null =>
  pkg && pkg.sizeMode !== 'custom' ? packageDimensions(pkg) : null;

/** What the ship / bundle-ship mutation is sent for one package. Only call for a usable package. */
export const packageShipInput = (pkg: ShipPackageDraft) => ({
  weight: parseFloat(String(pkg.weight)),
  dimensions: carrierDimensions(packingDimensions(pkg) || [])
});

/** 1-based numbers of the packages that cannot ship yet (no saved size chosen or added). */
export const packagesNeedingSize = (packages: ShipPackageDraft[]) =>
  packages.flatMap((pkg, i) => (packingDimensions(pkg) ? [] : [i + 1]));
